import { For, Show, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import type { AdminApi } from "../../api/client";
import { errorMessage, isRefreshRequired } from "../../api/errors";
import { ConfirmAction } from "../../components/ConfirmAction";
import { CopyText } from "../../components/CopyText";
import { Modal } from "../../components/Modal";
import { PanelState } from "../../components/PanelState";
import { StatusBadge } from "../../components/StatusBadge";
import type { GatewayKey, Relay, RelayState, RuntimePanel } from "../../model";
import { createAsyncPanel, type AsyncPanel } from "../../state/async";
import type { QuotaBoard } from "../../state/quota";
import { formatBytes, formatDate, formatNumber } from "../../utils/format";
import { QuotaDetailsModal, QuotaSummary } from "../quota/QuotaPanel";

type RelayDialog =
  | { kind: "create" }
  | { kind: "rename"; relay: Relay }
  | { kind: "connection"; relay: Relay }
  | { kind: "state"; relay: Relay; state: RelayState }
  | { kind: "details"; relay: Relay }
  | { kind: "quota"; relay: Relay }
  | null;

interface RelayPanelProps {
  api: AdminApi;
  panel: AsyncPanel<Relay[]>;
  keys: GatewayKey[];
  quotas: QuotaBoard;
  notify: (tone: "success" | "error" | "info", message: string) => void;
}

function relayTone(state: RelayState): "good" | "warning" | "neutral" {
  if (state === "active") return "good";
  if (state === "disabled") return "warning";
  return "neutral";
}

function relayStateLabel(state: RelayState): string {
  return { active: "Active", disabled: "Disabled", retired: "Retired" }[state];
}

function validateDisplayName(value: string): string | null {
  const length = [...value].length;
  if (value.trim() === "" || length > 80) return "Display name 必须包含 1 到 80 个字符。";
  return null;
}

function validateConnection(baseURL: string, credential: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(baseURL);
  } catch {
    return "Base URL 必须是绝对 HTTP(S) URL。";
  }
  if (
    !["http:", "https:"].includes(parsed.protocol) ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.search !== "" ||
    parsed.hash !== ""
  ) {
    return "Base URL 不能包含凭据、query 或 fragment。";
  }
  const bytes = new TextEncoder().encode(credential).byteLength;
  const hasControlCharacter = /\p{Cc}/u.test(credential);
  if (bytes < 8 || bytes > 512 || hasControlCharacter) {
    return "Data Plane Credential 必须包含 8 到 512 个非控制字节。";
  }
  return null;
}

export function RelayPanel(props: RelayPanelProps): JSX.Element {
  const [dialog, setDialog] = createSignal<RelayDialog>(null);
  const relays = () => props.panel.state().data ?? [];

  function chooseDialog(value: Exclude<RelayDialog, null>): void {
    setDialog(value);
  }

  async function mutate(action: () => Promise<void>, success: string): Promise<boolean> {
    try {
      await action();
      props.notify("success", success);
      await props.panel.refresh();
      return true;
    } catch (error) {
      props.notify("error", errorMessage(error));
      if (isRefreshRequired(error)) await props.panel.refresh();
      return false;
    }
  }

  function stateImpacts(relay: Relay, state: RelayState): string[] {
    if (state === "disabled") {
      return [
        "Disabled 不迁移已有 Session Binding。",
        "仍绑定到该 Relay 的会话会失败，直到重新启用或显式 eviction。",
      ];
    }
    if (state === "retired") {
      return [
        "该 Relay 将从所有新绑定候选中退出。",
        "相关 Session Binding 会在下一次请求时选择剩余 active eligible Relay。",
        "如果没有候选 Relay，请求会失败。后端没有不可逆状态机。",
      ];
    }
    return [
      `${relay.displayName} 将重新成为新 Session Binding 的候选节点。`,
      "已有绑定不会因为重新启用而自动改变。",
    ];
  }

  return (
    <section class="bento bento--table" aria-labelledby="relays-heading">
      <header class="bento__header">
        <div>
          <p class="eyebrow">Relay Registry</p>
          <h2 id="relays-heading">Relay 管理</h2>
        </div>
        <button type="button" class="button button--primary" onClick={() => setDialog({ kind: "create" })}>
          创建 Relay
        </button>
      </header>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Relay</th>
              <th>状态</th>
              <th>Base URL</th>
              <th>上游配额</th>
              <th>State Fingerprint</th>
              <th>更新时间</th>
              <th><span class="sr-only">操作</span></th>
            </tr>
          </thead>
          <tbody>
            <For each={relays()} fallback={<tr><td colSpan={7}><div class="empty-state"><strong>暂无 Relay</strong><span>创建第一个 Relay Registry 条目。</span></div></td></tr>}>
              {(relay) => (
                <tr>
                  <td>
                    <button type="button" class="entity-link" onClick={() => chooseDialog({ kind: "details", relay })}>
                      <strong>{relay.displayName}</strong><code>{relay.id}</code>
                    </button>
                  </td>
                  <td><StatusBadge label={relayStateLabel(relay.state)} tone={relayTone(relay.state)} /></td>
                  <td><CopyText value={relay.baseURL} label="URL" onError={() => props.notify("error", "复制失败。")} /></td>
                  <td>
                    <button type="button" class="quota-cell" onClick={() => chooseDialog({ kind: "quota", relay })}>
                      <QuotaSummary entry={props.quotas.entry(relay.id)} />
                    </button>
                  </td>
                  <td><CopyText value={relay.stateFingerprint} label="Fingerprint" onError={() => props.notify("error", "复制失败。")}/></td>
                  <td>{formatDate(relay.updatedAtMS)}</td>
                  <td>
                    <RelayActionMenu relay={relay} onChoose={chooseDialog} />
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <PanelState state={props.panel.state()} onRetry={() => void props.panel.refresh()} />

      <Show when={dialog()?.kind === "create"}>
        <RelayCreateDialog
          api={props.api}
          onClose={() => setDialog(null)}
          onCreated={async () => {
            props.notify("success", "Relay 已创建。Data Plane Credential 未被保留。");
            await props.panel.refresh();
          }}
          notify={props.notify}
        />
      </Show>
      <Show when={dialog()?.kind === "rename"}>
        <RelayRenameDialog
          relay={(dialog() as Extract<RelayDialog, { kind: "rename" }>).relay}
          onClose={() => setDialog(null)}
          onSubmit={(relay, name) => mutate(() => props.api.relays.rename(relay.id, name), "Relay 名称已更新。")}
        />
      </Show>
      <Show when={dialog()?.kind === "connection"}>
        <RelayConnectionDialog
          relay={(dialog() as Extract<RelayDialog, { kind: "connection" }>).relay}
          onClose={() => setDialog(null)}
          onSubmit={(relay, baseURL, credential) =>
            mutate(() => props.api.relays.updateConnection(relay.id, baseURL, credential), "Relay 连接已更新。")
          }
        />
      </Show>
      <Show keyed when={dialog()?.kind === "state" ? (dialog() as Extract<RelayDialog, { kind: "state" }>) : null}>
        {(value) => (
          <ConfirmAction
            title={`将 Relay 设为 ${relayStateLabel(value.state)}`}
            target={value.relay.displayName}
            confirmLabel={`确认设为 ${relayStateLabel(value.state)}`}
            impacts={stateImpacts(value.relay, value.state)}
            strong={value.state === "retired"}
            tone={value.state === "retired" ? "danger" : "primary"}
            onCancel={() => setDialog(null)}
            onConfirm={() =>
              mutate(
                () => props.api.relays.setState(value.relay.id, value.state),
                `Relay 已设为 ${relayStateLabel(value.state)}。`,
              )
            }
          />
        )}
      </Show>
      <Show when={dialog()?.kind === "details"}>
        <RelayDetailsModal
          api={props.api}
          relay={(dialog() as Extract<RelayDialog, { kind: "details" }>).relay}
          keys={props.keys}
          onClose={() => setDialog(null)}
        />
      </Show>
      <Show keyed when={dialog()?.kind === "quota" ? (dialog() as Extract<RelayDialog, { kind: "quota" }>) : null}>
        {(value) => (
          <QuotaDetailsModal
            relay={value.relay}
            entry={props.quotas.entry(value.relay.id)}
            onRefresh={() => void props.quotas.load(value.relay.id)}
            onClose={() => setDialog(null)}
          />
        )}
      </Show>
    </section>
  );
}

function positionActionMenu(menu: HTMLElement, trigger: HTMLElement): void {
  const viewportPadding = 12;
  const gap = 5;
  const triggerRect = trigger.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  const left = Math.max(
    viewportPadding,
    Math.min(triggerRect.right - menuRect.width, window.innerWidth - viewportPadding - menuRect.width),
  );
  const below = triggerRect.bottom + gap;
  const top = below + menuRect.height <= window.innerHeight - viewportPadding
    ? below
    : Math.max(viewportPadding, triggerRect.top - gap - menuRect.height);
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
}

function RelayActionMenu(props: {
  relay: Relay;
  onChoose: (dialog: Exclude<RelayDialog, null>) => void;
}): JSX.Element {
  let trigger!: HTMLButtonElement;
  let menu!: HTMLDivElement;

  onMount(() => {
    let frame = 0;
    const syncPosition = (): void => {
      if (!menu.matches(":popover-open")) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const triggerRect = trigger.getBoundingClientRect();
        const triggerOutsideViewport = triggerRect.bottom < 0
          || triggerRect.top > window.innerHeight
          || triggerRect.right < 0
          || triggerRect.left > window.innerWidth;
        if (triggerOutsideViewport) menu.hidePopover();
        else positionActionMenu(menu, trigger);
      });
    };
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);
    onCleanup(() => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
    });
  });

  function toggle(): void {
    if (menu.matches(":popover-open")) menu.hidePopover();
    else menu.showPopover();
  }

  return (
    <div class="action-menu">
      <button
        ref={trigger}
        type="button"
        class="action-menu__trigger"
        aria-haspopup="menu"
        onClick={toggle}
      >
        操作
      </button>
      <div
        ref={menu}
        popover="auto"
        role="menu"
        class="action-menu__menu"
        onToggle={(event) => {
          if (event.currentTarget.matches(":popover-open")) positionActionMenu(event.currentTarget, trigger);
        }}
        onClick={(event) => {
          if ((event.target as Element).closest("button")) event.currentTarget.hidePopover();
        }}
      >
        <button type="button" role="menuitem" onClick={() => props.onChoose({ kind: "details", relay: props.relay })}>查看详情</button>
        <button type="button" role="menuitem" onClick={() => props.onChoose({ kind: "rename", relay: props.relay })}>修改名称</button>
        <Show when={props.relay.state === "disabled"}>
          <button type="button" role="menuitem" onClick={() => props.onChoose({ kind: "connection", relay: props.relay })}>修改连接</button>
        </Show>
        <hr />
        <For each={(["active", "disabled", "retired"] as RelayState[]).filter((state) => state !== props.relay.state)}>
          {(state) => (
            <button
              type="button"
              role="menuitem"
              class={state === "retired" ? "action-menu__danger" : ""}
              onClick={() => props.onChoose({ kind: "state", relay: props.relay, state })}
            >
              设为 {relayStateLabel(state)}
            </button>
          )}
        </For>
      </div>
    </div>
  );
}

function RelayCreateDialog(props: {
  api: AdminApi;
  onClose: () => void;
  onCreated: () => Promise<void>;
  notify: RelayPanelProps["notify"];
}): JSX.Element {
  const [displayName, setDisplayName] = createSignal("");
  const [baseURL, setBaseURL] = createSignal("");
  const [fingerprint, setFingerprint] = createSignal("");
  const [credential, setCredential] = createSignal("");
  const [formError, setFormError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);

  async function submit(): Promise<void> {
    const nameError = validateDisplayName(displayName());
    const connectionError = validateConnection(baseURL(), credential());
    const fingerprintBytes = new TextEncoder().encode(fingerprint()).byteLength;
    if (nameError || connectionError || fingerprintBytes < 1 || fingerprintBytes > 256) {
      setFormError(nameError ?? connectionError ?? "State Fingerprint 必须包含 1 到 256 个字节。");
      return;
    }
    const secret = credential();
    setCredential("");
    setBusy(true);
    setFormError(null);
    try {
      await props.api.relays.create({
        displayName: displayName(),
        baseURL: baseURL(),
        stateFingerprint: fingerprint(),
        dataPlaneCredential: secret,
      });
      await props.onCreated();
      props.onClose();
    } catch (error) {
      setFormError(errorMessage(error));
      props.notify("error", errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="创建 Relay"
      eyebrow="Relay Registry"
      onClose={props.onClose}
      busy={busy()}
      footer={
        <>
          <button type="button" class="button button--ghost" disabled={busy()} onClick={props.onClose}>取消</button>
          <button type="button" class="button button--primary" disabled={busy()} onClick={() => void submit()}>
            {busy() ? "正在创建…" : "创建 Relay"}
          </button>
        </>
      }
    >
      <div class="form-grid">
        <label class="field field--wide"><span>Display name</span><input maxlength={80} value={displayName()} onInput={(event) => setDisplayName(event.currentTarget.value)} /></label>
        <label class="field field--wide"><span>Base URL</span><input type="url" placeholder="https://relay.example.test" value={baseURL()} onInput={(event) => setBaseURL(event.currentTarget.value)} /></label>
        <label class="field field--wide"><span>State Fingerprint</span><input autocomplete="off" value={fingerprint()} onInput={(event) => setFingerprint(event.currentTarget.value)} /></label>
        <label class="field field--wide">
          <span>Data Plane Credential</span>
          <input type="password" autocomplete="new-password" value={credential()} onInput={(event) => setCredential(event.currentTarget.value)} />
          <small>提交时立即从表单清除；成功响应和列表都不会返回该值。</small>
        </label>
      </div>
      <Show when={formError()}>{(message) => <p class="callout callout--danger">{message()}</p>}</Show>
    </Modal>
  );
}

function RelayRenameDialog(props: {
  relay: Relay;
  onClose: () => void;
  onSubmit: (relay: Relay, name: string) => Promise<boolean>;
}): JSX.Element {
  const [name, setName] = createSignal(props.relay.displayName);
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  async function submit(): Promise<void> {
    const validation = validateDisplayName(name());
    if (validation) {
      setError(validation);
      return;
    }
    setBusy(true);
    if (await props.onSubmit(props.relay, name())) props.onClose();
    setBusy(false);
  }
  return (
    <Modal title="修改 Relay 名称" onClose={props.onClose} busy={busy()} footer={<><button class="button button--ghost" type="button" onClick={props.onClose}>取消</button><button class="button button--primary" type="button" disabled={busy()} onClick={() => void submit()}>保存</button></>}>
      <label class="field"><span>Display name</span><input maxlength={80} value={name()} onInput={(event) => setName(event.currentTarget.value)} /></label>
      <Show when={error()}>{(message) => <p class="callout callout--danger">{message()}</p>}</Show>
    </Modal>
  );
}

function RelayConnectionDialog(props: {
  relay: Relay;
  onClose: () => void;
  onSubmit: (relay: Relay, baseURL: string, credential: string) => Promise<boolean>;
}): JSX.Element {
  const [baseURL, setBaseURL] = createSignal(props.relay.baseURL);
  const [credential, setCredential] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  async function submit(): Promise<void> {
    const validation = validateConnection(baseURL(), credential());
    if (validation) {
      setError(validation);
      return;
    }
    const secret = credential();
    setCredential("");
    setBusy(true);
    if (await props.onSubmit(props.relay, baseURL(), secret)) props.onClose();
    setBusy(false);
  }
  return (
    <Modal title="修改 Relay 连接" eyebrow="仅 Disabled Relay" onClose={props.onClose} busy={busy()} footer={<><button class="button button--ghost" type="button" onClick={props.onClose}>取消</button><button class="button button--primary" type="button" disabled={busy()} onClick={() => void submit()}>更新连接</button></>}>
      <p class="callout callout--warning">Base URL 与 Data Plane Credential 将同时提交。Credential 不会回显。</p>
      <div class="form-grid">
        <label class="field field--wide"><span>Base URL</span><input type="url" value={baseURL()} onInput={(event) => setBaseURL(event.currentTarget.value)} /></label>
        <label class="field field--wide"><span>Data Plane Credential</span><input type="password" autocomplete="new-password" value={credential()} onInput={(event) => setCredential(event.currentTarget.value)} /></label>
      </div>
      <Show when={error()}>{(message) => <p class="callout callout--danger">{message()}</p>}</Show>
    </Modal>
  );
}

function RelayDetailsModal(props: { api: AdminApi; relay: Relay; keys: GatewayKey[]; onClose: () => void }): JSX.Element {
  const status = createAsyncPanel(() => props.api.relays.status(props.relay.id));
  onMount(() => void status.refresh());
  const eligibleKeys = () => props.keys.filter((key) => key.eligibleRelayIDs.includes(props.relay.id));
  return (
    <Modal title={props.relay.displayName} eyebrow="Relay 详情" onClose={props.onClose} size="wide">
      <div class="detail-grid">
        <section>
          <h3>Registry</h3>
          <dl class="detail-list">
            <dt>ID</dt><dd><code>{props.relay.id}</code></dd>
            <dt>State</dt><dd><StatusBadge label={relayStateLabel(props.relay.state)} tone={relayTone(props.relay.state)} /></dd>
            <dt>Base URL</dt><dd><code>{props.relay.baseURL}</code></dd>
            <dt>Fingerprint</dt><dd><code>{props.relay.stateFingerprint}</code></dd>
            <dt>Updated</dt><dd>{formatDate(props.relay.updatedAtMS)}</dd>
          </dl>
        </section>
        <section>
          <h3>Eligible Gateway Keys</h3>
          <Show when={eligibleKeys().length > 0} fallback={<p>当前没有 Gateway Key 将此 Relay 列入 Eligible Relay Set。</p>}>
            <ul class="plain-list"><For each={eligibleKeys()}>{(key) => <li><strong>{key.label}</strong><code>{key.publicID}</code></li>}</For></ul>
          </Show>
        </section>
      </div>
      <section class="detail-section">
        <div class="section-heading"><h3>Relay Status</h3><button class="text-button" type="button" onClick={() => void status.refresh()}>刷新</button></div>
        <Show when={status.state().status === "idle" || status.state().status === "loading"}>
          <p class="callout">正在获取 Relay Status…</p>
        </Show>
        <Show when={status.state().data}>
          {(view) => (
            <div class="detail-grid detail-grid--three">
              <dl class="detail-list">
                <dt>Credential</dt><dd>{view().credentialStatus}</dd>
                <dt>Provider</dt><dd>{view().credentialProvider}</dd>
                <dt>Account ID</dt><dd>{view().upstreamAccountID ?? "未知"}</dd>
                <dt>Generation</dt><dd>{view().credentialGeneration ?? "未知"}</dd>
              </dl>
              <dl class="detail-list">
                <dt>Operational</dt><dd>{formatNumber(view().operational.admission.operational)}</dd>
                <dt>Transformation</dt><dd>{formatNumber(view().operational.admission.transformation)}</dd>
                <dt>Waiting</dt><dd>{formatNumber(view().operational.admission.waiting)}</dd>
                <dt>In-flight</dt><dd>{formatNumber(view().operational.http.inFlight)}</dd>
              </dl>
              <dl class="detail-list">
                <dt>Transform workers</dt><dd>{formatNumber(view().operational.transformation.workers)}</dd>
                <dt>Transform active</dt><dd>{formatNumber(view().operational.transformation.active)}</dd>
                <dt>Transform queue</dt><dd>{formatNumber(view().operational.transformation.queueDepth)} / {formatNumber(view().operational.transformation.queueCapacity)}</dd>
                <dt>Credential expires</dt><dd>{formatDate(view().credentialExpiresAtMS)}</dd>
              </dl>
              <RelayRuntimeDetails runtime={view().operational.runtime} />
            </div>
          )}
        </Show>
        <PanelState state={status.state()} onRetry={() => void status.refresh()} />
      </section>
    </Modal>
  );
}

function RelayRuntimeDetails(props: { runtime: RuntimePanel }): JSX.Element {
  return (
    <Show
      when={props.runtime.status === "available" ? props.runtime.snapshot : null}
      fallback={<p class="callout callout--warning">Runtime Snapshot 不可用或版本不受支持。</p>}
    >
      {(runtime) => (
        <dl class="detail-list">
          <dt>Runtime</dt><dd>{runtime().runtimeKind} / schema v{runtime().schemaVersion}</dd>
          <dt>Collection</dt><dd>{runtime().collectionStatus}</dd>
          <dt>RSS</dt><dd>{formatOptionalRuntimeBytes(runtime().process.residentMemoryBytes)}</dd>
          <dt>Virtual memory</dt><dd>{formatOptionalRuntimeBytes(runtime().process.virtualMemoryBytes)}</dd>
          <dt>Threads</dt><dd>{formatOptionalRuntimeNumber(runtime().process.threads)}</dd>
          <dt>Open FDs</dt><dd>{formatOptionalRuntimeNumber(runtime().process.openFileDescriptors)}</dd>
          <dt>Tasks / I/O workers</dt><dd>{formatNumber(runtime().execution.tasksActive)} / {formatNumber(runtime().execution.ioWorkers)}</dd>
        </dl>
      )}
    </Show>
  );
}

function formatOptionalRuntimeNumber(value: number | null): string {
  return value === null ? "不可用" : formatNumber(value);
}

function formatOptionalRuntimeBytes(value: number | null): string {
  return value === null ? "不可用" : formatBytes(value);
}
